import { useEffect, useState, useRef } from "react";
import { Camera, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

export default function SickNotesPage() {
  const { user, hasPerm } = useAuth();
  const [notes, setNotes] = useState([]);
  const [sickLeaves, setSickLeaves] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const isReviewer = hasPerm("sicknotes.review");

  const load = () => {
    api.get("/sick-notes").then((r) => setNotes(r.data)).catch(() => {});
    api.get("/timeoff").then((r) => setSickLeaves(r.data.filter((t) => t.type === "sick" && t.user_id === user.id))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const uploadedIds = new Set(notes.filter((n) => n.user_id === user.id).map((n) => n.timeoff_id));
  const needingNote = sickLeaves.filter((s) => !uploadedIds.has(s.id) && s.status !== "denied");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLeave) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("timeoff_id", selectedLeave);
    fd.append("file", file);
    try {
      const { data } = await api.post("/sick-notes", fd);
      toast.success(data.on_time ? "Doctor's note uploaded — sent for admin review" : "Note uploaded but flagged LATE (due the day you returned)");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const review = async (id, status) => {
    try {
      await api.put(`/sick-notes/${id}/review`, { status, comment: "" });
      toast.success(`Note ${status}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
  };

  const reviewQueue = notes.filter((n) => n.status === "pending");

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Compliance</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Doctor's notes</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Paid sick leave requires a doctor's note photographed with your phone camera, uploaded the day you return. An assigned admin reviews each note.
        </p>
      </div>

      <div className="bg-card border border-border rounded-md p-6">
        <h3 className="font-heading font-semibold tracking-tight mb-4">Upload a doctor's note</h3>
        {needingNote.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-sick-leave-msg">No sick leave awaiting a note. Request sick leave first from the Time Off page.</p>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <p className="text-sm font-medium mb-1.5">Sick leave</p>
              <Select value={selectedLeave} onValueChange={setSelectedLeave}>
                <SelectTrigger data-testid="sick-leave-select"><SelectValue placeholder="Select the sick leave" /></SelectTrigger>
                <SelectContent>
                  {needingNote.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{fmtDate(s.start_date)} → {fmtDate(s.end_date)} ({s.total_hours}h)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="sick-note-file-input" />
            <Button data-testid="sick-note-upload-btn" disabled={!selectedLeave || busy} onClick={() => fileRef.current?.click()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-10">
              <Camera size={17} className="mr-2" /> {busy ? "Uploading…" : "Take photo of note"}
            </Button>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">On your phone this opens the camera directly. Photos only — one photo per page of the note.</p>
      </div>

      {isReviewer && reviewQueue.length > 0 && (
        <div className="bg-card border border-border rounded-md">
          <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Review queue</h3></div>
          <div className="divide-y divide-border">
            {reviewQueue.map((n) => (
              <div key={n.id} data-testid={`note-review-row-${n.id}`} className="px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <button onClick={() => setPreview(n)} data-testid={`note-preview-btn-${n.id}`} className="h-14 w-14 rounded-md border border-border overflow-hidden shrink-0 hover:opacity-80 transition-opacity">
                    <img src={fileUrl(n.file_path)} alt="note" className="h-full w-full object-cover" />
                  </button>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{n.user_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {n.timeoff ? `${fmtDate(n.timeoff.start_date)} → ${fmtDate(n.timeoff.end_date)}` : ""} · uploaded {fmtDate(n.uploaded_at)}
                      {!n.on_time && <span className="text-red-600 font-semibold"> · LATE</span>}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`note-approve-btn-${n.id}`} size="sm" onClick={() => review(n.id, "approved")} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check size={15} className="mr-1" /> Approve</Button>
                  <Button data-testid={`note-reject-btn-${n.id}`} size="sm" variant="outline" onClick={() => review(n.id, "rejected")} className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"><X size={15} className="mr-1" /> Reject</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-md">
        <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">{isReviewer ? "All notes" : "My notes"}</h3></div>
        <div className="divide-y divide-border" data-testid="sick-notes-list">
          {notes.map((n) => (
            <div key={n.id} className="px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <button onClick={() => setPreview(n)} className="h-10 w-10 rounded-md border border-border overflow-hidden shrink-0">
                  <img src={fileUrl(n.file_path)} alt="note" className="h-full w-full object-cover" />
                </button>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{isReviewer ? n.user_name : "Doctor's note"}</p>
                  <p className="text-xs text-muted-foreground">Uploaded {fmtDate(n.uploaded_at)}{!n.on_time && <span className="text-red-600 font-semibold"> · LATE</span>}</p>
                </div>
              </div>
              <Badge className={STATUS_STYLE[n.status]}>{n.status}</Badge>
            </div>
          ))}
          {notes.length === 0 && <p className="px-6 py-8 text-sm text-muted-foreground">No doctor's notes uploaded yet.</p>}
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="note-preview-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Doctor's note</DialogTitle></DialogHeader>
          {preview && <img src={fileUrl(preview.file_path)} alt="Doctor's note" className="w-full rounded-md border border-border" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
